// SPDX-License-Identifier: AGPL-3.0-or-later

// LOCAL-ONLY: every user-visible string of select mode and the relocate panel, in one place, word for word what
// Obyrus shows too (relocation-plan.md §6.4). English only, like the rest of the panel. Exclude from upstream sync.

export const RelocationStrings = {
	/** The message menu's entry, and the panel's title. */
	relocateMessages: 'Relocate messages',
	/** The one close action: ends relocation (menu only, no header buttons; the user's decision 2026-10-30). */
	closeRelocation: 'Close relocation mode',
	startLabel: 'Start message',
	startPlaceholder: 'Select a message to set the start',
	endLabel: 'End message',
	endPlaceholder: 'Select another message to set the end',
	resetSelection: 'Reset selection',
	destServerLabel: 'Destination server',
	directMessages: 'Direct Messages',
	destChannelLabel: 'Destination channel',
	destChannelPlaceholder: 'Pick a channel…',
	relocate: 'Relocate',
	counting: 'Counting…',
	cancel: 'Cancel',
	recentMoves: 'Recent moves',
	loading: 'Loading…',
	noRecentMoves: 'No recent moves',
	undo: 'Undo',
	resume: 'Resume',
	open: 'Open',
	stopAfterBatch: 'Stop after this batch',
	stoppedPartWay: 'Stopped part-way',
	undone: 'Undone',
	partlyUndone: 'Partly undone',
	movedBack: 'Moved back',
} as const;

const plural = (count: number, one: string, other: string) => (count === 1 ? one : other);

/** LOCAL-ONLY: the selection's count, as the server answered it. Exclude from upstream sync. */
export type SelectionCount = {state: 'counting'} | {state: 'ready'; count: number} | {state: 'error'; message: string};

/** The server's exact count of the selected range (never a partial count). */
export function selectedCountText(count: number): string {
	return `${count.toLocaleString()} ${plural(count, 'message', 'messages')} selected`;
}

export function confirmMoveText(count: number, source: string, destination: string): string {
	return `Move ${count.toLocaleString()} ${plural(count, 'message', 'messages')} from ${source} to ${destination}?`;
}

export const BATCH_LINE = 'They move in batches of 500. You can stop after any batch.';

/**
 * The confirmation's lines, in order: the question, then the batch line when the range is over one batch. There is no
 * warning about the destination's name: the trash-channel line was removed by the user's decision (2026-10-30), and
 * Obyrus shows exactly these lines too (relocation-plan.md, browser and Obyrus stay feature-identical).
 */
export function confirmMoveLines(count: number, source: string, destination: string, batchSize = 500): Array<string> {
	const lines = [confirmMoveText(count, source, destination)];
	if (count > batchSize) lines.push(BATCH_LINE);
	return lines;
}
// The server's own text for RELOCATION_MESSAGE_NOT_MOVABLE (packages/errors ErrorI18nMessages.ts).
export const NOT_MOVABLE_LINE = "Thread starters, forum posts and published announcements can't be relocated.";
export const EMPTY_RANGE = 'There are no messages in that range.';

export function progressText(moved: number, total: number | null): string {
	if (total == null) return `Moving… ${moved.toLocaleString()} moved`;
	return `Moving… ${moved.toLocaleString()} of ${total.toLocaleString()}`;
}

export function movingBackText(moved: number): string {
	return `Moving back… ${moved.toLocaleString()} moved`;
}

export function movedText(count: number, destination: string): string {
	return `Moved ${count.toLocaleString()} ${plural(count, 'message', 'messages')} to ${destination}.`;
}

export function stoppedText(moved: number, total: number | null): string {
	if (total == null) return `Stopped after ${moved.toLocaleString()} ${plural(moved, 'message', 'messages')}.`;
	return `Stopped after ${moved.toLocaleString()} of ${total.toLocaleString()} messages.`;
}

export const NOTHING_MOVED = 'Nothing in that range is still here to move.';

export function fileFailuresText(count: number): string {
	return `${count.toLocaleString()} ${plural(count, 'file', 'files')} could not be moved; their messages moved.`;
}

export function confirmUndoText(count: number, destination: string, source: string): string {
	return `Move ${count.toLocaleString()} ${plural(count, 'message', 'messages')} from ${destination} back to ${source}?`;
}

export function movedBackText(count: number, source: string): string {
	return `Moved ${count.toLocaleString()} ${plural(count, 'message', 'messages')} back to ${source}.`;
}

export function logMetaText(count: number, who: string, when: string, batches: number): string {
	const base = `${count.toLocaleString()} ${plural(count, 'message', 'messages')} · ${who} · ${when}`;
	return batches > 1 ? `${base} · ${batches} batches` : base;
}

export function errorText(detail: string): string {
	return `Error: ${detail}`;
}

export function requestFailedText(status: number): string {
	return `The request failed (HTTP ${status}).`;
}

/**
 * The selection's count line once both ends are set: "Counting…" while the server counts, then its exact count,
 * or the server's error text. Shared by the desktop panel and the phone-width bar.
 */
export function selectionCountText(selectionCount: SelectionCount | null): string {
	if (selectionCount == null || selectionCount.state === 'counting') return RelocationStrings.counting;
	if (selectionCount.state === 'error') return selectionCount.message;
	return selectedCountText(selectionCount.count);
}
